import React, { useEffect, useRef, useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, useDisclosure, Badge, Flex, Text, Box,
  HStack, Select, Input, Avatar, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import deportesApi, { Deportista, API_UPLOADS } from '../../../api/deportesApi';
import FormularioDeportista from './FormularioDeportista';
import ModalParticipaciones from './ModalParticipaciones';
import {
  useCatalogos, Paginacion, fmtFecha, calcularEdad, nombreCompleto, ROJO, ROJO_HOVER,
} from '../_shared';

const TablaDeportistas: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { disciplinas, categorias, localidades } = useCatalogos();

  const { isOpen: isVer, onOpen: onVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isEditar, onOpen: onEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isNuevo, onOpen: onNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isHist, onOpen: onHist, onClose: onCloseHist } = useDisclosure();
  const { isOpen: isEliminar, onOpen: onEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [sel, setSel] = useState<Deportista | null>(null);
  const [data, setData] = useState<Deportista[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // filtros: nombre, disciplina, categoria, localidad, equipo, edad, sexo
  const [fNombre, setFNombre] = useState('');
  const [fDisciplina, setFDisciplina] = useState('');
  const [fCategoria, setFCategoria] = useState('');
  const [fLocalidad, setFLocalidad] = useState('');
  const [fEquipo, setFEquipo] = useState('');
  const [fEdad, setFEdad] = useState('');
  const [fSexo, setFSexo] = useState('');

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await deportesApi.listarDeportistas({
        nombre: fNombre,
        disciplinaId: fDisciplina,
        categoriaId: fCategoria,
        localidadId: fLocalidad,
        sexo: fSexo,
      });
      setData(res || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  // recarga con filtros de servidor (debounce simple)
  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fNombre, fDisciplina, fCategoria, fLocalidad, fSexo]);

  const edadOf = (d: Deportista) => d.edad ?? calcularEdad(d.fechaNacimiento);

  // filtros de cliente: equipo (texto) y edad exacta
  const datosFiltrados = data.filter((d) => {
    const matchEquipo = fEquipo ? (d.equipo?.nombre || '').toLowerCase().includes(fEquipo.toLowerCase()) : true;
    const matchEdad = fEdad ? edadOf(d) === Number(fEdad) : true;
    return matchEquipo && matchEdad;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const abrir = async (item: Deportista, accion: 'ver' | 'editar' | 'hist') => {
    try {
      const full = await deportesApi.obtenerDeportista(item.id);
      setSel(full);
    } catch {
      setSel(item);
    }
    if (accion === 'ver') onVer();
    else if (accion === 'editar') onEditar();
    else onHist();
  };

  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };

  const handleEliminar = async () => {
    if (!sel) return;
    setProcesando(true);
    try {
      await deportesApi.eliminarDeportista(sel.id);
      toast({ title: 'Deportista eliminado', status: 'success', duration: 3000 });
      onCloseEliminar();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const exportExcel = () => {
    const filas = datosFiltrados.map((d) => ({
      Nombre: nombreCompleto(d),
      Edad: edadOf(d) ?? '',
      Sexo: d.sexo || '',
      CURP: d.curp || '',
      Disciplina: d.disciplina?.nombre || '',
      Categoría: d.categoria?.nombre || '',
      Equipo: d.equipo?.nombre || '',
      Localidad: d.localidad || '',
      Teléfono: d.telefono || d.celular || '',
      Estatus: d.estatus || '',
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Deportistas');
    XLSX.writeFile(wb, `Deportistas_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const [generandoPdf, setGenerandoPdf] = useState(false);
  const reportePdf = async () => {
    setGenerandoPdf(true);
    try {
      const blob = await deportesApi.reporteDeportistasPdf({
        nombre: fNombre, disciplinaId: fDisciplina, categoriaId: fCategoria,
        localidadId: fLocalidad, sexo: fSexo, edad: fEdad,
      });
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'No se pudo generar el PDF.', status: 'error', duration: 4000 });
    } finally { setGenerandoPdf(false); }
  };

  const selInput = { size: 'sm' as const };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={2} flexWrap="wrap">
          <Input placeholder="Buscar por nombre..." value={fNombre} onChange={(e) => { setFNombre(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '200px' }} />
          <Select placeholder="Disciplina" value={fDisciplina} onChange={(e) => { setFDisciplina(e.target.value); setCurrentPage(1); }} {...selInput} w="150px">
            {disciplinas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </Select>
          <Select placeholder="Categoría" value={fCategoria} onChange={(e) => { setFCategoria(e.target.value); setCurrentPage(1); }} {...selInput} w="140px">
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
          <Select placeholder="Localidad" value={fLocalidad} onChange={(e) => { setFLocalidad(e.target.value); setCurrentPage(1); }} {...selInput} w="150px">
            {localidades.map((l: any) => <option key={l.id} value={l.id}>{l.descripcion}</option>)}
          </Select>
          <Input placeholder="Equipo" value={fEquipo} onChange={(e) => { setFEquipo(e.target.value); setCurrentPage(1); }} size="sm" w="120px" />
          <Input placeholder="Edad" type="number" value={fEdad} onChange={(e) => { setFEdad(e.target.value); setCurrentPage(1); }} size="sm" w="90px" />
          <Select placeholder="Sexo" value={fSexo} onChange={(e) => { setFSexo(e.target.value); setCurrentPage(1); }} {...selInput} w="120px">
            <option value="Masculino">Masculino</option>
            <option value="Femenino">Femenino</option>
            <option value="Otro">Otro</option>
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={exportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<DownloadIcon />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={reportePdf} isLoading={generandoPdf} loadingText="Generando...">Reporte PDF</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={onNuevo}>Nuevo deportista</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={ROJO}>
            <Tr>
              <Th color="white" fontSize="xs">Deportista</Th>
              <Th color="white" fontSize="xs">Edad</Th>
              <Th color="white" fontSize="xs">Sexo</Th>
              <Th color="white" fontSize="xs">Disciplina</Th>
              <Th color="white" fontSize="xs">Categoría</Th>
              <Th color="white" fontSize="xs">Equipo</Th>
              <Th color="white" fontSize="xs">Localidad</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={9} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={9} textAlign="center" py={8}><Text color="gray.400">No se encontraron deportistas</Text></Td></Tr>
            ) : (
              itemsPagina.map((d, idx) => (
                <Tr key={d.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td>
                    <HStack spacing={2}>
                      <Avatar size="sm" name={nombreCompleto(d)} src={d.fotografia ? `${API_UPLOADS}${d.fotografia}` : undefined} />
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{nombreCompleto(d)}</Text>
                        {d.curp && <Text fontSize="2xs" color="gray.400">{d.curp}</Text>}
                      </Box>
                    </HStack>
                  </Td>
                  <Td>{edadOf(d) != null ? `${edadOf(d)}` : '—'}</Td>
                  <Td>{d.sexo || '—'}</Td>
                  <Td>{d.disciplina?.nombre || '—'}</Td>
                  <Td>{d.categoria?.nombre || '—'}</Td>
                  <Td>{d.equipo?.nombre || '—'}</Td>
                  <Td>{d.localidad || '—'}</Td>
                  <Td><Badge colorScheme={d.estatus === 'activo' ? 'green' : 'gray'} borderRadius="full" px={2}>{d.estatus}</Badge></Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => abrir(d, 'ver')}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => abrir(d, 'editar')}>Editar</Button>
                      <Button size="xs" colorScheme="teal" variant="outline" onClick={() => abrir(d, 'hist')}>Historial</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSel(d); onEliminar(); }}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <Paginacion currentPage={currentPage} totalPages={totalPages} total={datosFiltrados.length} indexPrimero={indexPrimero} indexUltimo={indexUltimo} onChange={setCurrentPage} />

      {/* Ver */}
      <Modal isOpen={isVer} onClose={onCloseVer} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Detalle del Deportista</ModalHeader>
          <ModalBody py={5}>{sel && <FormularioDeportista modo="view" data={sel} />}</ModalBody>
          <ModalFooter><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={isEditar} onClose={onCloseEditar} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Editar Deportista</ModalHeader>
          <ModalBody py={5}>{sel && <FormularioDeportista modo="edit" data={sel} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={isNuevo} onClose={onCloseNuevo} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>Nuevo Deportista</ModalHeader>
          <ModalBody py={5}><FormularioDeportista modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} /></ModalBody>
        </ModalContent>
      </Modal>

      <ModalParticipaciones isOpen={isHist} onClose={onCloseHist} deportista={sel} />

      {/* Eliminar */}
      <AlertDialog isOpen={isEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={ROJO}>Eliminar deportista</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar a <strong>{sel ? nombreCompleto(sel) : ''}</strong>? Esta acción lo marcará como inactivo.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaDeportistas;
